import { z } from "zod";
import { connectorCategorySlugs } from "./connectorCategories.ts";
import {
  type ConnectorCatalogItem,
  connectorVisibility,
  type ConnectorVisibility,
} from "./connectors.ts";

/**
 * Per-tool exposure policy: `disabled` (never exposed), `confirm` (requires user
 * approval per call), `allow` (runs freely). Mirrors `McpToolPolicy` in
 * `schemas/mcpServers.ts` (kept in sync; both are the same string union).
 */
export const mcpToolPolicy = z.enum(["disabled", "confirm", "allow"]);
export type McpToolPolicy = z.infer<typeof mcpToolPolicy>;

/**
 * How users authenticate to a server: `oauth` (the OAuth 2.1 / DCR / CIMD
 * discovery flow), `api_key` (the user pastes a personal API key we send as a
 * bearer token), or `none` (anonymous MCP server). Mirrors `McpAuthMode` in
 * `schemas/mcpServers.ts` (kept in sync).
 */
export const mcpAuthMode = z.enum(["oauth", "api_key", "none"]);
export type McpAuthMode = z.infer<typeof mcpAuthMode>;

/**
 * Protocol-specific behavior layered on top of MCP. Standard MCP keeps the
 * existing initialize/tools-list/tools-call flow. UCP catalog rows use the UCP
 * shopping-catalog binding and inject the public agent profile at call time.
 */
export const mcpProtocolProfile = z.enum(["standard", "ucp_catalog"]);
export type McpProtocolProfile = z.infer<typeof mcpProtocolProfile>;

export const mcpRegistrySource = z.enum(["manual", "composio"]);
export type McpRegistrySource = z.infer<typeof mcpRegistrySource>;

/**
 * Safe, app-internal outcomes for an MCP OAuth callback. Provider-controlled
 * error text is never part of this contract.
 */
export const mcpOAuthErrorCodes = [
  "mcp_cancelled",
  "mcp_admin_action_required",
  "mcp_permissions_changed",
  "mcp_provider_unavailable",
  "mcp_authorization_failed",
] as const;
export type McpOAuthErrorCode = (typeof mcpOAuthErrorCodes)[number];

export const isMcpOAuthErrorCode = (value: unknown): value is McpOAuthErrorCode =>
  typeof value === "string" && mcpOAuthErrorCodes.some((code) => code === value);

export const mcpAdminAuthChoice = z.enum([
  "oauth_2_1",
  "oauth_manual",
  "api_key",
  "none",
  "ucp_catalog",
]);
export type McpAdminAuthChoice = z.infer<typeof mcpAdminAuthChoice>;

export const mcpDiscoveredTool = z.object({
  toolName: z.string().min(1).max(500),
  wordName: z.string().min(1).max(500).optional(),
  /**
   * Loose bound, not a content policy. Tool descriptions are written verbatim
   * from whatever a server's `tools/list` returns (`syncMcpToolCatalog`), with
   * no cap — so any limit here that a real server can exceed makes exporting a
   * catalog and importing it back fail. Notion and Composio already ship
   * single tools past 8k. This only exists to stop a pathological upload.
   */
  description: z.string().max(50_000),
  writeGated: z.boolean(),
});
export type McpDiscoveredTool = z.infer<typeof mcpDiscoveredTool>;

/** Admin request to allowlist a new MCP server (admin service, `/s/mcp-servers`). */
export const registerMcpServerReq = z.object({
  name: z.string().min(1).max(100),
  url: z.string().url().max(2000),
  serviceUrl: z.string().url().max(2000).optional(),
  description: z.string().max(500),
  selectionCriteria: z.string().min(1).max(500),
  authMode: mcpAuthMode.default("oauth"),
  protocolProfile: mcpProtocolProfile.default("standard"),
  visibility: connectorVisibility.optional(),
  /** Category slugs to tag this server with at registration. Validated against
   *  the `connector_categories` vocabulary by the handler. */
  categories: connectorCategorySlugs.optional(),
  // Optional pre-registered OAuth app credentials, for servers that require you to
  // register an app yourself (no DCR/CIMD) — e.g. Gong.
  clientId: z.string().max(2000).optional(),
  clientSecret: z.string().max(4096).optional(),
});
export type RegisterMcpServerReq = z.infer<typeof registerMcpServerReq>;

export type AdminMcpServerExamplesSummary = {
  total: number;
  active: number;
  failed: number;
};

/** A registered server as the admin console sees it (all servers, with status). */
export type AdminMcpServer = {
  id: number;
  name: string;
  slug: string;
  description: string;
  /** Free text telling the agent when to pick this server's dynamic module. */
  selectionCriteria: string;
  url: string;
  serviceUrl: string;
  enabled: boolean;
  visibility: ConnectorVisibility;
  /** Category slugs (from `connector_categories`) this server is tagged with. */
  categories: string[];
  authMode: McpAuthMode;
  protocolProfile: McpProtocolProfile;
  registrySource: McpRegistrySource;
  /** Non-secret project-level Composio Auth Config binding, when required. */
  composioAuthConfigId: string | null;
  /** True once discovery + DCR have populated the OAuth config (OAuth mode only). */
  oauthConfigured: boolean;
  /** Last OAuth discovery/registration error (why a server is "pending"), or null. */
  discoveryError: string | null;
  /** Dynamic Forthic example generation summary for the server's tools. */
  exampleSummary: AdminMcpServerExamplesSummary;
  /** Number of discovered tools currently cached for this server. */
  toolCount: number;
  /** Real user connections, excluding admin-test discovery connections. */
  connectedCount: number;
  pending: boolean;
  createdAt: string;
  updatedAt: string | null;
};

export type ListAdminMcpServersResponse = { servers: AdminMcpServer[] };

/** A discovered tool plus the admin's current expose/hide choice (one checklist row). */
export type AdminMcpToolChoice = {
  toolName: string;
  wordName: string;
  description: string;
  /** Name-derived "this tool can mutate" hint, shown as a badge in the admin UI. */
  writeGated: boolean;
  /** The tool's effective policy (explicit override, or the dynamic-tool default). */
  policy: McpToolPolicy;
};

export const mcpForthicExampleGenerationStatus = z.enum(["generated", "edited", "failed"]);
export type McpForthicExampleGenerationStatus = z.infer<typeof mcpForthicExampleGenerationStatus>;

export type AdminMcpForthicExample = {
  id: number;
  toolName: string;
  wordName: string;
  active: boolean;
  englishInstructions: string;
  forthicTranslation: string;
  args: Record<string, unknown>;
  generationStatus: McpForthicExampleGenerationStatus;
  generationPromptVersion: string | null;
  generatedAt: string | null;
  generatedByModel: string | null;
  lastGenerationError: string | null;
  adminEdited: boolean;
  editedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
};

/** Full detail for one server — the admin Manage panel (editable fields + checklist). */
export type AdminMcpServerDetail = AdminMcpServer & {
  /** True if any real (non-admin-test) user has connected; locks URL edits. */
  connected: boolean;
  /**
   * Main-app URL the admin opens to OAuth this server in discovery mode. Creates
   * an admin-test connection that populates the tool list without those tools
   * reaching the admin's own agent or Connected Accounts.
   */
  discoverUrl: string;
  /** The cached tool set, each flagged with its current expose/hide state. */
  tools: AdminMcpToolChoice[];
  /** Dynamic Forthic examples searched alongside static examples at runtime. */
  examples: AdminMcpForthicExample[];
  /** Configured OAuth client id (DCR-registered, CIMD URL, or admin-provided) —
   *  shown so the admin can edit it. Not secret. */
  clientId: string | null;
  /** Whether an OAuth client secret is stored (the secret itself is never returned). */
  hasClientSecret: boolean;
  oauthClientMode: "auto" | "manual" | null;
};

export type AdminMcpServerDetailResponse = { server: AdminMcpServerDetail };

/** Admin edit of an existing server. A URL change is rejected once a real user has connected. */
export const updateMcpServerReq = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().min(1).max(500).optional(),
  selectionCriteria: z.string().min(1).max(500).optional(),
  url: z.string().url().max(2000).optional(),
  serviceUrl: z.string().url().max(2000).optional(),
  authMode: mcpAuthMode.optional(),
  protocolProfile: mcpProtocolProfile.optional(),
  visibility: connectorVisibility.optional(),
  /** Category slugs to tag this server with. Validated against the
   *  `connector_categories` vocabulary by the handler. */
  categories: connectorCategorySlugs.optional(),
  /** Pre-registered OAuth app credentials (manual-client servers). An omitted or
   *  empty clientSecret on edit keeps the stored one. */
  clientId: z.string().max(2000).optional(),
  clientSecret: z.string().max(4096).optional(),
  /** Force an auto OAuth 2.1 server to discard its cached DCR/CIMD client and
   *  register/discover again. Existing user grants will need reconnect. */
  resetOAuthClient: z.boolean().optional(),
  /** Per-tool overrides; tools absent from the map use the dynamic-tool default. */
  toolPolicies: z.record(z.string(), mcpToolPolicy).optional(),
});
export type UpdateMcpServerReq = z.infer<typeof updateMcpServerReq>;

export const setMcpServerEnabledReq = z.object({ enabled: z.boolean() });
export type SetMcpServerEnabledReq = z.infer<typeof setMcpServerEnabledReq>;

export const updateMcpForthicExampleReq = z.object({
  englishInstructions: z.string().min(1).max(2000).optional(),
  args: z.record(z.string(), z.unknown()).optional(),
  active: z.boolean().optional(),
});
export type UpdateMcpForthicExampleReq = z.infer<typeof updateMcpForthicExampleReq>;

export type UpdateMcpForthicExampleResponse = { example: AdminMcpForthicExample };

export type GenerateMcpForthicExamplesResponse = {
  generated: number;
  skipped: number;
  failed: number;
};

export type RegenerateMcpForthicExamplesResponse = {
  regenerated: number;
  failed: number;
};

export const suggestMcpServerReq = z
  .object({
    /** Back-compat alias for older callers; interpreted as an MCP URL. */
    url: z.string().url().max(2000).optional(),
    /** Optional MCP endpoint URL; when present, suggestion also detects auth. */
    mcpUrl: z.string().url().max(2000).optional(),
    /** Public website URL used to scrape service metadata. */
    serviceUrl: z.string().url().max(2000).optional(),
  })
  .refine((value) => value.serviceUrl || value.mcpUrl || value.url, {
    message: "serviceUrl or mcpUrl is required",
    path: ["serviceUrl"],
  });
export type SuggestMcpServerReq = z.infer<typeof suggestMcpServerReq>;

export type SuggestedMcpAuthDetection = {
  status: "oauth_2_1" | "manual_oauth" | "no_auth" | "ucp_catalog" | "unknown";
  source: "www_authenticate" | "well_known" | "origin" | "mcp_probe" | "none";
  issuer: string | null;
  scopes: string[] | null;
  supportsClientIdMetadataDocument: boolean;
  supportsDynamicClientRegistration: boolean;
  error: string | null;
};

export type SuggestMcpServerResponse = {
  canonicalUrl: string | null;
  serviceUrl: string;
  suggestions: {
    name: string;
    description: string | null;
    selectionCriteria: string | null;
    authChoice: McpAdminAuthChoice | null;
  };
  authDetection: SuggestedMcpAuthDetection;
  warnings: string[];
};

// ── Admin: MCP server catalog export/import ─────────────────────────

export const MCP_SERVER_CATALOG_SCHEMA_VERSION = 1;

export const mcpServerCatalogSource = z.object({
  service: z.string().min(1),
  environment: z.string().optional(),
  apiUrl: z.string().optional(),
  publicUrl: z.string().optional(),
});
export type McpServerCatalogSource = z.infer<typeof mcpServerCatalogSource>;

export const mcpServerCatalogServerSource = z.object({
  id: z.number().int().positive().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type McpServerCatalogServerSource = z.infer<typeof mcpServerCatalogServerSource>;

export const mcpServerCatalogServer = z.object({
  name: z.string().min(1).max(100),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9_-]+$/),
  description: z.string().max(500),
  url: z.string().url().max(2000),
  serviceUrl: z.string().url().max(2000).optional(),
  enabled: z.boolean(),
  visibility: connectorVisibility.optional(),
  authMode: mcpAuthMode,
  protocolProfile: mcpProtocolProfile.default("standard"),
  discoveredTools: z.array(mcpDiscoveredTool),
  toolPolicies: z.record(z.string(), mcpToolPolicy),
  source: mcpServerCatalogServerSource.optional(),
});
export type McpServerCatalogServer = z.infer<typeof mcpServerCatalogServer>;

export const mcpServerCatalogFile = z.object({
  schemaVersion: z.literal(MCP_SERVER_CATALOG_SCHEMA_VERSION),
  exportedAt: z.string(),
  source: mcpServerCatalogSource.optional(),
  servers: z.array(mcpServerCatalogServer),
});
export type McpServerCatalogFile = z.infer<typeof mcpServerCatalogFile>;

export const importMcpServerCatalogResponse = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type ImportMcpServerCatalogResponse = z.infer<typeof importMcpServerCatalogResponse>;

// ── Custom MCP servers: a user registers a server for their own account ─────

export const suggestCustomMcpServerReq = z.object({
  url: z.string().url().max(2000),
});
export type SuggestCustomMcpServerReq = z.infer<typeof suggestCustomMcpServerReq>;

/**
 * What a probe of the server found about signing in. `admin_required` is a
 * server that runs OAuth but won't register us as a client, so only an admin
 * with a pre-registered app can add it; `unknown` means the user must choose.
 */
export const customMcpAuthChoice = z.enum([
  "oauth",
  "api_key",
  "none",
  "admin_required",
  "unknown",
]);
export type CustomMcpAuthChoice = z.infer<typeof customMcpAuthChoice>;

export type SuggestCustomMcpServerResponse = {
  /** The server URL as it will be stored, or null when it could not be canonicalised. */
  canonicalUrl: string;
  serviceUrl: string;
  suggestions: {
    name: string;
    description: string | null;
    selectionCriteria: string | null;
  };
  authChoice: CustomMcpAuthChoice;
  /**
   * A server at this URL the account can already connect: a shared one it may
   * see, or its own custom one. The client offers Connect instead of Add.
   */
  existingServerId: number | null;
  warnings: string[];
};

export const registerCustomMcpServerReq = z.object({
  url: z.string().url().max(2000),
  /** Each falls back to the probe's suggestion when omitted. */
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  selectionCriteria: z.string().min(1).max(500).optional(),
  /** Falls back to the detected auth; required when detection is inconclusive. */
  authMode: mcpAuthMode.optional(),
  /** For an api_key server, connects in the same call. */
  apiKey: z.string().min(1).max(8192).optional(),
});
export type RegisterCustomMcpServerReq = z.infer<typeof registerCustomMcpServerReq>;

export type RegisterCustomMcpServerResponse = {
  /** The new server as a catalog tile, with its connect path or credential path. */
  item: ConnectorCatalogItem;
  /** True when the call also connected it (api_key with a key supplied). */
  connected: boolean;
  /**
   * Why an inline API-key connect did not succeed: the server rejected the key,
   * or it could not be reached and the key was kept. Null otherwise.
   */
  connectionError: string | null;
};

/** Addresses one of the account's custom servers on `/api/a/mcp/custom/:id`. */
export const customMcpServerIdParam = z.object({
  id: z.coerce.number().int().positive(),
});
export type CustomMcpServerIdParam = z.infer<typeof customMcpServerIdParam>;

export const updateCustomMcpServerReq = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  selectionCriteria: z.string().min(1).max(500).optional(),
});
export type UpdateCustomMcpServerReq = z.infer<typeof updateCustomMcpServerReq>;

export type UpdateCustomMcpServerResponse = { item: ConnectorCatalogItem };
